import PersonIcon from '@mui/icons-material/Person';
import SearchIcon from '@mui/icons-material/Search';
import {
  InputBase,
  List,
  ListItem,
  ListItemText,
  ThemeProvider,
} from '@mui/material';
import { action } from 'mobx';
import React, { useRef, useState } from 'react';
import i18n from '../../../../i18n';
import { defaultTheme } from '../../theme/default-theme';
import { lightInputTheme } from '../../theme/mui-theme';
import { formatUserSearchBasedOnDisplayNameAndLocale } from '../../utilFunctions/formatters';
import { styled } from '../styled';
import PopUpMenu from '../utils/PopUpMenu';

import ListItemButton from '@mui/material/ListItemButton';
import { useEmployeeSearch } from '../../v2/api/employee/hooks';
import { Employee } from '../../v2/api/employee/types';
import { BetterSuspense } from '../../v2/common/components/BetterSuspense';
import { LoadingCircle } from '../../v2/common/components/LoadingCircle';
import {
  useGlobalLocaleContext,
  useLatestEmployeeVersionContext,
} from '../../v2/context/contexts';
import { EmployeeProfile } from '../employee-profile/EmployeeProfile';

const SearchWrapper = styled('div')`
  position: relative;
  height: 25px;
  border-radius: 4px;
  background-color: ${defaultTheme.color.light}26;
  &:hover {
    background-color: ${defaultTheme.color.light}40;
  }
  margin: 0 8px;
  width: auto;
`;

const IconWrapper = styled.div`
  width: 40px;
  height: 100%;
  position: absolute;
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SearchInput = styled(InputBase)`
  width: 100%;
  height: 25px !important;
  font-size: 0.8em;

  & .input {
    padding: 8px 8px 8px 32px;
    transition: all 0.2s;
    width: 0;
    height: 25px;

    &:focus {
      padding-left: 40px;
      width: 200px;
    }
  }
` as typeof InputBase;

const StyledSearchIcon = styled(SearchIcon)`
  && {
    width: 0.8em;
    height: 0.8em;
  }
` as typeof SearchIcon;

const StyledPersonIcon = styled(PersonIcon)`
  && {
    width: 0.8em;
    height: 0.8em;
  }
` as typeof PersonIcon;

const StyledList = styled(List)`
  min-width: 245px;
  max-width: 245px;
`;

const StyledEmployeeList = styled(List)`
  min-width: 230px;
  max-width: 230px;
`;

const StyledListItemText = styled(ListItemText)`
  && {
    white-space: normal;
    word-wrap: break-word;
    overflow-wrap: break-word;

    .MuiListItemText-primary,
    .MuiListItemText-secondary {
      text-overflow: ellipsis;
      overflow: hidden;
    }
  }
`;

const SingleElementStyledListWrapper = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  return (
    <StyledList>
      <ListItem>
        <StyledListItemText>{children}</StyledListItemText>
      </ListItem>
    </StyledList>
  );
};

interface PopUpMenuContentProps {
  selectedEmployeeId: string | null;
  employeeSearchText: string;
  handleClick: (employeeId: string) => void;
  handleClose: () => void;
}

const PopUpMenuContent = (props: PopUpMenuContentProps) => {
  const { selectedEmployeeId, employeeSearchText, handleClick, handleClose } =
    props;
  const locale = useGlobalLocaleContext();
  const latestEmployeeVersion = useLatestEmployeeVersionContext();
  const { data: searchedEmployees } = useEmployeeSearch(
    employeeSearchText,
    latestEmployeeVersion
  );
  const getNameFromEmployeeHit = (emp: Employee) => {
    return formatUserSearchBasedOnDisplayNameAndLocale(emp, locale.selected);
  };

  return employeeSearchText.length > 0 && employeeSearchText.length < 2 ? (
    <SingleElementStyledListWrapper>
      <StyledListItemText>Type at least 2 characters</StyledListItemText>
    </SingleElementStyledListWrapper>
  ) : searchedEmployees.length > 0 ? (
    <StyledEmployeeList>
      {searchedEmployees.map((emp) => {
        // If local name exists, showing local name first
        return (
          <ListItemButton key={emp.id} onClick={() => handleClick(emp.id)}>
            <StyledListItemText {...getNameFromEmployeeHit(emp)} />
          </ListItemButton>
        );
      })}
    </StyledEmployeeList>
  ) : (
    <SingleElementStyledListWrapper>
      <StyledListItemText>No results</StyledListItemText>
    </SingleElementStyledListWrapper>
  );
};

export const Search = () => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [employeeSearchText, setEmployeeSearchText] = useState('');
  const searchWrapperRef = useRef<HTMLDivElement | null>(null);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string | null>(
    null
  );

  const handleInputClick = action(() => {
    if (anchorEl) {
      setAnchorEl(null);
    } else {
      setTimeout(() => {
        setAnchorEl(searchWrapperRef.current);
      }, 100);
    }
  });

  const handleClose = () => {
    setSelectedEmployeeId(null);
  };

  const handleClick = (employeeId: string) => {
    setSelectedEmployeeId(employeeId);
    setAnchorEl(null);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmployeeSearchText(event.target.value);
  };

  return (
    <>
      <ThemeProvider theme={lightInputTheme}>
        <SearchWrapper ref={searchWrapperRef}>
          <IconWrapper>
            <>
              <StyledSearchIcon sx={{ color: 'primary.contrastText' }} />
              <StyledPersonIcon sx={{ color: 'primary.contrastText' }} />
            </>
          </IconWrapper>
          <SearchInput
            placeholder={i18n.t('common:shell.searchEmployee')}
            classes={{ input: 'input' }}
            onClick={handleInputClick}
            onChange={handleChange}
            className={'intercom_appbar_search'}
          />
        </SearchWrapper>
      </ThemeProvider>
      <PopUpMenu
        anchorEl={anchorEl}
        open={Boolean(anchorEl) && employeeSearchText.length > 0}
        onClose={() => setAnchorEl(null)}
        placement={'bottom-end'}
      >
        <BetterSuspense
          wait={0}
          fallback={
            <SingleElementStyledListWrapper>
              <LoadingCircle />
            </SingleElementStyledListWrapper>
          }
        >
          <PopUpMenuContent
            selectedEmployeeId={selectedEmployeeId}
            employeeSearchText={employeeSearchText}
            handleClick={handleClick}
            handleClose={handleClose}
          />
        </BetterSuspense>
      </PopUpMenu>
      {selectedEmployeeId && (
        <EmployeeProfile
          employeeId={selectedEmployeeId}
          onClose={handleClose}
        />
      )}
    </>
  );
};

export default Search;
